import React, { useState, useRef, useEffect } from 'react';
import { supabase } from '../lib/supabaseClient';

interface FleetGPSProps {
  drivers: any[];
  data: any[];
  companyId: string | null;
}

export default function FleetGPS({ drivers, data, companyId }: FleetGPSProps) {
  return (
    <div>
      {/* KPI STRIP */}
      <div style={{ display:"grid", gridTemplateColumns:"repeat(4,1fr)", gap:"10px", marginBottom:"20px" }}>
        {[
          {label:"TOTAL DRIVERS",value:drivers.length,color:"#ccc"},
          {label:"DRIVING",value:drivers.filter(d=>d.hos_status==="DRIVING").length,color:"#4488FF"},
          {label:"ON DUTY",value:drivers.filter(d=>d.hos_status==="ON DUTY").length,color:"#FFD700"},
          {label:"OFF DUTY",value:drivers.filter(d=>d.hos_status==="OFF DUTY"||!d.hos_status).length,color:"#777"},
        ].map((k,i)=>(
          <div key={i} style={{ border:"1px solid #1a1a1a", background:"#050505", padding:"14px" }}>
            <div style={{ color:"#777", fontSize:"9px", letterSpacing:"1.5px", marginBottom:"6px" }}>{k.label}</div>
            <div style={{ color:k.color, fontSize:"20px", fontWeight:900 }}>{k.value}</div>
          </div>
        ))}
      </div>

      {/* LIVE MAP — full width, Google Maps JS API with driver pins */}
      <div style={{ marginBottom:"16px", border:"1px solid #1e1e1e", background:"#0a0a0a" }}>
        <div style={{ color:"#32CD32", fontSize:"10px", letterSpacing:"2px", fontWeight:700, padding:"12px 16px", borderBottom:"1px solid #1e1e1e" }}>LIVE FLEET MAP</div>
        <FleetMapPanel drivers={drivers} loads={data} apiKey={process.env.REACT_APP_GOOGLE_MAPS_API_KEY||''} />
      </div>

      {/* DRIVER STATUS BOARD */}
      <div style={{ border:"1px solid #1e1e1e", padding:"20px", background:"#0a0a0a" }}>
        <div style={{ color:"#32CD32", fontSize:"10px", letterSpacing:"2px", fontWeight:700, marginBottom:"14px" }}>DRIVER STATUS BOARD</div>
        {drivers.length === 0 ? (
          <div style={{ color:"#555", fontSize:"12px" }}>No drivers found. Add drivers in Driver Management.</div>
        ) : (
          <div style={{ display:"grid", gridTemplateColumns:"repeat(3,1fr)", gap:"10px" }}>
            {drivers.map((d,i)=>{
              const hosColor = d.hos_status==="DRIVING"?"#4488FF":d.hos_status==="ON DUTY"?"#FFD700":"#777";
              const activeLoad = data.find((l:any)=>l.assigned_driver_id===d.id&&['ACTIVE','IN TRANSIT'].includes((l.status||'').toUpperCase()));
              return (
                <div key={i} style={{ border:`1px solid ${hosColor}22`, background:"#050505", padding:"12px" }}>
                  <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start" }}>
                    <div style={{ flex:1 }}>
                      <div style={{ fontWeight:700, fontSize:"13px" }}>{d.full_name}</div>
                      <div style={{ color:"#555", fontSize:"10px", marginTop:"2px" }}>{d.current_city&&d.current_state?`${d.current_city}, ${d.current_state}`:"Location unknown"}</div>
                      {activeLoad && (
                        <div style={{ color:"#32CD32", fontSize:"10px", marginTop:"4px" }}>
                          ▶ Load #{activeLoad.load_number} → {activeLoad.delivery_city},{activeLoad.delivery_state}
                        </div>
                      )}
                    </div>
                    <span style={{ color:hosColor, fontWeight:700, fontSize:"10px", border:`1px solid ${hosColor}`, padding:"2px 6px", whiteSpace:"nowrap" }}>{d.hos_status||"OFF DUTY"}</span>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

// ─── FLEET MAP PANEL ──────────────────────────────────────────────────────────
// Uses Google Maps JS API to render a real map with colored truck pins for each driver
export function FleetMapPanel({ drivers, loads, apiKey }: { drivers: any[]; loads: any[]; apiKey: string }) {
  const mapRef = useRef<HTMLDivElement>(null);
  const mapInstanceRef = useRef<any>(null);
  const markersRef = useRef<any[]>([]);
  const [mapError, setMapError] = useState<string|null>(null);

  // US city lat/lng lookup for common cities (fallback when no GPS coords)
  const cityCoords: Record<string, [number,number]> = {
    'Atlanta,GA':[33.749,-84.388],'Dallas,TX':[32.776,-96.797],'Houston,TX':[29.760,-95.370],
    'Chicago,IL':[41.878,-87.630],'Los Angeles,CA':[34.052,-118.244],'New York,NY':[40.713,-74.006],
    'Miami,FL':[25.775,-80.208],'Phoenix,AZ':[33.448,-112.074],'Denver,CO':[39.739,-104.984],
    'Seattle,WA':[47.606,-122.332],'Nashville,TN':[36.162,-86.781],'Memphis,TN':[35.149,-90.048],
    'Charlotte,NC':[35.227,-80.843],'Jacksonville,FL':[30.332,-81.656],'Indianapolis,IN':[39.768,-86.158],
    'Columbus,OH':[39.961,-82.999],'Kansas City,MO':[39.099,-94.578],'San Antonio,TX':[29.425,-98.494],
    'Austin,TX':[30.267,-97.743],'Fort Worth,TX':[32.755,-97.330],'El Paso,TX':[31.761,-106.485],
    'Louisville,KY':[38.252,-85.759],'Baltimore,MD':[39.290,-76.612],'Milwaukee,WI':[43.038,-87.906],
    'Albuquerque,NM':[35.085,-106.651],'Tucson,AZ':[32.222,-110.926],'Fresno,CA':[36.737,-119.787],
    'Sacramento,CA':[38.581,-121.494],'Mesa,AZ':[33.415,-111.831],'Omaha,NE':[41.257,-95.995],
    'Cleveland,OH':[41.499,-81.695],'Raleigh,NC':[35.779,-78.638],'Minneapolis,MN':[44.977,-93.265],
    'Tulsa,OK':[36.154,-95.993],'Tampa,FL':[27.947,-82.459],'New Orleans,LA':[29.951,-90.072],
    'Laredo,TX':[27.506,-99.507],'Amarillo,TX':[35.221,-101.831],'Lubbock,TX':[33.578,-101.855],
  };

  const getCoords = (city: string, state: string): [number,number]|null => {
    if (!city || !state) return null;
    const key = `${city.trim()},${state.trim().toUpperCase()}`;
    return cityCoords[key] || null;
  };

  useEffect(() => {
    if (!apiKey) { setMapError('No Google Maps API key configured.'); return; }

    const initMap = () => {
      if (!mapRef.current || !window.google) return;
      try {
        const map = new window.google.maps.Map(mapRef.current, {
          center: { lat: 39.5, lng: -98.35 },
          zoom: 4,
          styles: [
            { elementType:'geometry', stylers:[{color:'#1a1a1a'}] },
            { elementType:'labels.text.fill', stylers:[{color:'#32CD32'}] },
            { elementType:'labels.text.stroke', stylers:[{color:'#000'}] },
            { featureType:'road', elementType:'geometry', stylers:[{color:'#2a2a2a'}] },
            { featureType:'road.highway', elementType:'geometry', stylers:[{color:'#333'}] },
            { featureType:'water', elementType:'geometry', stylers:[{color:'#0a0a0a'}] },
            { featureType:'administrative', elementType:'geometry.stroke', stylers:[{color:'#32CD3244'}] },
          ],
          disableDefaultUI: false,
          mapTypeControl: false,
          streetViewControl: false,
        });
        mapInstanceRef.current = map;

        // Drop a pin for each driver that has a city/state
        markersRef.current.forEach(m => m.setMap(null));
        markersRef.current = [];

        drivers.forEach(d => {
          // PRIORITY 1: Use real GPS lat/lng from Motive ELD sync
          // PRIORITY 2: Fall back to city/state lookup table
          let lat: number | null = null;
          let lng: number | null = null;
          let locationSource = 'city';

          if (d.current_lat && d.current_lng) {
            lat = Number(d.current_lat);
            lng = Number(d.current_lng);
            locationSource = 'motive';
          } else {
            const coords = getCoords(d.current_city, d.current_state);
            if (coords) { lat = coords[0]; lng = coords[1]; }
          }

          if (lat === null || lng === null) return; // No location data at all

          const isActive = loads.some((l:any) => l.assigned_driver_id === d.id && ['ACTIVE','IN TRANSIT'].includes((l.status||'').toUpperCase()));
          const isDriving = d.hos_status === 'DRIVING';
          const isOnDuty = d.hos_status === 'ON DUTY';
          const pinColor = isDriving ? '#4488FF' : isOnDuty ? '#FFD700' : isActive ? '#32CD32' : '#555';
          const activeLoad = loads.find((l:any) => l.assigned_driver_id === d.id && ['ACTIVE','IN TRANSIT'].includes((l.status||'').toUpperCase()));
          const lastSync = d.last_eld_sync ? new Date(d.last_eld_sync).toLocaleTimeString() : null;

          const marker = new window.google.maps.Marker({
            position: { lat, lng },
            map,
            title: d.full_name,
            icon: {
              path: window.google.maps.SymbolPath.FORWARD_CLOSED_ARROW,
              scale: locationSource === 'motive' ? 7 : 5,
              fillColor: pinColor,
              fillOpacity: 1,
              strokeColor: locationSource === 'motive' ? '#fff' : '#000',
              strokeWeight: locationSource === 'motive' ? 2 : 1,
            },
          });

          const infoWindow = new window.google.maps.InfoWindow({
            content: `<div style="background:#0a0a0a;border:1px solid ${pinColor};padding:12px;font-family:monospace;font-size:11px;color:#fff;min-width:180px">
              <div style="color:${pinColor};font-weight:700;margin-bottom:6px;font-size:13px">${d.full_name}</div>
              <div style="color:#aaa;margin-bottom:4px">${d.current_city||'?'}, ${d.current_state||'?'}</div>
              <div style="color:${pinColor};font-weight:700;margin-bottom:4px">${d.hos_status||'OFF DUTY'}</div>
              ${activeLoad ? `<div style="color:#32CD32;margin-bottom:4px">&#9654; Load #${activeLoad.load_number}</div>` : ''}
              ${lastSync ? `<div style="color:#555;font-size:9px">ELD sync: ${lastSync}</div>` : ''}
              ${locationSource === 'motive' ? '<div style="color:#32CD32;font-size:9px">&#9679; LIVE GPS</div>' : '<div style="color:#555;font-size:9px">&#9675; city estimate</div>'}
            </div>`,
          });

          marker.addListener('click', () => infoWindow.open(map, marker));
          markersRef.current.push(marker);
        });

        // Also drop delivery destination pins for active loads
        loads.filter((l:any) => ['ACTIVE','IN TRANSIT'].includes((l.status||'').toUpperCase())).forEach((l:any) => {
          const coords = getCoords(l.delivery_city, l.delivery_state);
          if (!coords) return;
          const marker = new window.google.maps.Marker({
            position: { lat: coords[0], lng: coords[1] },
            map,
            title: `Delivery: ${l.delivery_city}, ${l.delivery_state}`,
            icon: {
              path: window.google.maps.SymbolPath.CIRCLE,
              scale: 5,
              fillColor: '#ff4444',
              fillOpacity: 0.8,
              strokeColor: '#fff',
              strokeWeight: 1,
            },
          });
          markersRef.current.push(marker);
        });

      } catch(e: any) {
        setMapError('Map failed to initialize: ' + e.message);
      }
    };

    if ((window as any).google && (window as any).google.maps) {
      initMap();
    } else {
      // Load Google Maps JS API dynamically
      const existingScript = document.getElementById('gmap-script');
      if (existingScript) {
        existingScript.addEventListener('load', initMap);
        return;
      }
      const script = document.createElement('script');
      script.id = 'gmap-script';
      script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places`;
      script.async = true;
      script.defer = true;
      script.onload = initMap;
      script.onerror = () => setMapError('Failed to load Google Maps. Check API key restrictions.');
      document.head.appendChild(script);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [drivers, loads, apiKey]);

  if (mapError) return (
    <div style={{ height:'400px', display:'flex', alignItems:'center', justifyContent:'center', color:'#ff4444', fontSize:'12px', padding:'20px', textAlign:'center' }}>
      {mapError}
    </div>
  );

  return <div ref={mapRef} style={{ width:'100%', height:'480px' }} />;
}

// ─── MOTIVE SYNC PANEL ────────────────────────────────────────────────────────
function MotiveSyncPanel({ companyId, drivers }: { companyId: string|null; drivers: any[] }) {
  const [syncing, setSyncing] = useState(false);
  const [syncResult, setSyncResult] = useState<{synced:number;errors:string[]}|null>(null);
  const [lastSync, setLastSync] = useState<string|null>(null);

  const runMotiveSync = async () => {
    if (!companyId) return;
    setSyncing(true); setSyncResult(null);
    const MOTIVE_KEY = process.env.REACT_APP_MOTIVE_API_KEY || '';
    const PROXY = 'https://corsproxy.io/?';
    let synced = 0;
    const errors: string[] = [];
    try {
      // Fetch drivers from Motive
      const resp = await fetch(`${PROXY}${encodeURIComponent('https://api.keeptruckin.com/v1/users?per_page=50&role=driver')}`, {
        headers: { 'X-Api-Key': MOTIVE_KEY, 'Accept': 'application/json' }
      });
      if (!resp.ok) throw new Error(`Motive API ${resp.status}: ${resp.statusText}`);
      const json = await resp.json();
      const motiveDrivers = json.users || [];
      // Try to match by name and update HOS status
      for (const md of motiveDrivers) {
        const firstName = md.user?.first_name || '';
        const lastName = md.user?.last_name || '';
        const fullName = `${firstName} ${lastName}`.trim();
        if (!fullName) continue;
        const match = drivers.find(d => d.full_name?.toLowerCase().includes(firstName.toLowerCase()) || d.full_name?.toLowerCase().includes(lastName.toLowerCase()));
        if (match) {
          const hosStatus = md.user?.current_duty_status === 'driving' ? 'DRIVING' : md.user?.current_duty_status === 'on_duty' ? 'ON DUTY' : md.user?.current_duty_status === 'sleeper_berth' ? 'SLEEPER' : 'OFF DUTY';
          const { error } = await supabase.from('drivers').update({ hos_status: hosStatus, eld_connected: true, eld_provider: 'Motive' }).eq('id', match.id);
          if (!error) synced++;
          else errors.push(`${fullName}: ${error.message}`);
        }
      }
      setSyncResult({ synced, errors });
      setLastSync(new Date().toLocaleTimeString());
    } catch (e: any) {
      setSyncResult({ synced: 0, errors: [e.message || 'Motive sync failed'] });
    }
    setSyncing(false);
  };

  return (
    <div style={{ border:'1px solid #1a3a1a', background:'#050505', padding:'16px', marginBottom:'20px' }}>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'10px' }}>
        <div>
          <div style={{ color:'#32CD32', fontSize:'10px', letterSpacing:'2px', fontWeight:700 }}>MOTIVE ELD SYNC</div>
          {lastSync && <div style={{ color:'#555', fontSize:'10px', marginTop:'2px' }}>Last sync: {lastSync}</div>}
        </div>
        <button onClick={runMotiveSync} disabled={syncing} style={{ background:syncing?'#1a1a1a':'#32CD32', color:syncing?'#555':'#000', border:'none', padding:'8px 16px', fontFamily:'monospace', fontSize:'10px', fontWeight:700, cursor:syncing?'not-allowed':'pointer', letterSpacing:'1px' }}>
          {syncing ? '⏳ SYNCING...' : '⟳ SYNC MOTIVE ELD'}
        </button>
      </div>
      {syncResult && (
        <div style={{ fontSize:'11px', color: syncResult.errors.length > 0 ? '#FFD700' : '#32CD32' }}>
          {syncResult.synced > 0 && <span>✓ {syncResult.synced} driver(s) synced</span>}
          {syncResult.errors.length > 0 && <span style={{ color:'#ff4444', marginLeft:'12px' }}>⚠ {syncResult.errors[0]}</span>}
          {syncResult.synced === 0 && syncResult.errors.length === 0 && <span style={{ color:'#555' }}>No matching drivers found in Motive</span>}
        </div>
      )}
    </div>
  );
}
